// Piezas compartidas de la hoja de vista previa (04-11-2026): lo que usan la hoja (HojaVistaPrevia.tsx) y sus cuerpos
// (VisorPdf, VisorImagenes, VisorTexto). Aparte para que ningún cuerpo importe la hoja que lo monta.
import { ChevronLeft, ChevronRight, Loader2, type LucideIcon } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Pista, TACTIL_44 } from '../ui';
import type { Apertura, ElementoVista } from './vistaPrevia';
import type { Zoom } from './zoom';

/** Lo que la hoja le pide al cuerpo que esté abierto (PDF, imágenes, texto) cuando Pau usa el teclado. */
export interface ControlCuerpo {
  anterior: () => void;
  siguiente: () => void;
  inicio: () => void;
  fin: () => void;
  /** El % que corresponde a «Ajustar» ahora mismo (para que + y − sigan desde ahí). */
  ajuste: () => number;
  /** Donde se desplaza con ↑/↓/Espacio. */
  desplazable: () => HTMLElement | null;
}

export interface PropsCuerpo {
  apertura: Apertura;
  elemento: ElementoVista;
  zoom: Zoom;
  alZoom: (z: Zoom) => void;
  carril: boolean;
}

export const BOTON = cn(
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-oficina-texto2 transition-colors duration-150 ease-o-apple hover:bg-oficina-control hover:text-oficina-texto',
  'outline-none focus-visible:ring-2 focus-visible:ring-oficina-texto',
  TACTIL_44,
);

export const EnlaceIcono = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement> & { icono: LucideIcon; etiqueta: string }>(function EnlaceIcono(
  { icono: Icono, etiqueta, className, ...resto },
  ref,
) {
  return (
    <Pista texto={etiqueta}>
      <a ref={ref} aria-label={etiqueta} className={cn(BOTON, className)} {...resto}>
        <Icono className="h-[18px] w-[18px]" aria-hidden />
      </a>
    </Pista>
  );
});

export function BotonBarra({ icono: Icono, etiqueta, onClick, activo, children }: { icono?: LucideIcon; etiqueta: string; onClick: () => void; activo?: boolean; children?: ReactNode }) {
  return (
    <Pista texto={etiqueta}>
      <button type="button" aria-label={etiqueta} aria-pressed={activo} onClick={onClick} className={cn(BOTON, activo && 'bg-oficina-control text-oficina-texto')}>
        {Icono ? <Icono className="h-[18px] w-[18px]" aria-hidden /> : children}
      </button>
    </Pista>
  );
}

export function Pildora({ actual, total, alAnterior, alSiguiente, despierta }: { actual: number; total: number; alAnterior: () => void; alSiguiente: () => void; despierta: number }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
    const reloj = setTimeout(() => setVisible(false), 2000);
    return () => clearTimeout(reloj);
  }, [despierta, actual]);
  return (
    <div
      data-testid="pildora-paginas"
      className={cn(
        'pointer-events-auto absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-oficina-control p-1 text-o-sub font-medium text-oficina-texto shadow-o-flota transition-opacity duration-300 ease-o-apple',
        !visible && 'pointer-events-none opacity-0',
      )}
    >
      <BotonBarra icono={ChevronLeft} etiqueta={t('oficinaV2.vistaPrevia.anterior')} onClick={alAnterior} />
      <span aria-live="polite" className="min-w-[4.5rem] text-center tabular-nums">
        {t('oficinaV2.vistaPrevia.paginaDe', { n: actual, total })}
      </span>
      <BotonBarra icono={ChevronRight} etiqueta={t('oficinaV2.vistaPrevia.siguiente')} onClick={alSiguiente} />
    </div>
  );
}

/** Un número que cambia cada vez que Pau mueve el ratón, desplaza o pulsa una tecla dentro de `zona` (despierta la píldora). */
export function useDespertar(zona: HTMLElement | null): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!zona) return;
    let pendiente = false;
    const tocar = () => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(() => {
        pendiente = false;
        setN((x) => x + 1);
      });
    };
    const eventos = ['pointermove', 'scroll', 'keydown', 'wheel'] as const;
    eventos.forEach((ev) => zona.addEventListener(ev, tocar, { passive: true }));
    return () => eventos.forEach((ev) => zona.removeEventListener(ev, tocar));
  }, [zona]);
  return n;
}

/**
 * Ctrl+rueda (y el pellizco del trackpad, que el navegador manda igual) y el pellizco con dos dedos (iPad): tamaño
 * continuo. Sin esto el navegador ampliaría la página entera.
 */
export function useGestosZoom(zona: HTMLElement | null, alMultiplicar: (factor: number) => void): void {
  const cb = useRef(alMultiplicar);
  cb.current = alMultiplicar;
  useEffect(() => {
    if (!zona) return;
    const rueda = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      cb.current(Math.exp(-e.deltaY * 0.01));
    };
    let distancia = 0;
    const dist = (e: TouchEvent) => Math.hypot(e.touches[0]!.clientX - e.touches[1]!.clientX, e.touches[0]!.clientY - e.touches[1]!.clientY);
    const empieza = (e: TouchEvent) => {
      if (e.touches.length === 2) distancia = dist(e);
    };
    const mueve = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !distancia) return;
      e.preventDefault();
      const d = dist(e);
      cb.current(d / distancia);
      distancia = d;
    };
    const acaba = () => {
      distancia = 0;
    };
    zona.addEventListener('wheel', rueda, { passive: false });
    zona.addEventListener('touchstart', empieza, { passive: true });
    zona.addEventListener('touchmove', mueve, { passive: false });
    zona.addEventListener('touchend', acaba);
    return () => {
      zona.removeEventListener('wheel', rueda);
      zona.removeEventListener('touchstart', empieza);
      zona.removeEventListener('touchmove', mueve);
      zona.removeEventListener('touchend', acaba);
    };
  }, [zona]);
}

export function Cargando() {
  return (
    <p className="flex h-full items-center justify-center gap-2 text-o-texto text-oficina-texto3">
      <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
      {t('oficinaV2.vistaPrevia.abriendo')}
    </p>
  );
}

export function NoSePudo() {
  return <p className="flex h-full items-center justify-center px-6 text-center text-o-texto text-oficina-texto3">{t('oficinaV2.vistaPrevia.error')}</p>;
}

